import { useContent } from "contexts/contentContext/contentContext.js";
import { useState, useRef, useEffect } from "react";
import useOnClickOutside from "hooks/useOnClickOutside.js";
import Icon from "../../Elements/Icon/Icon.tsx";

import "./SelectElement.scss";

const SelectElement = ({ list, item, setItem, title, id }) => {
  const elementRef = useRef();
  const [tempList, setTempList] = useState(list);
  const { getTextByKey } = useContent();
  const defaultTitle = getTextByKey(title || "select-country");
  const [tempItem, setTempItem] = useState(item ? item : "");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const itemRefs = useRef([]);

  const blurTimeout = useRef();

  useEffect(() => {
    if (item !== null && !item !== undefined) {
      setTempItem(item);
    }
  }, [item]);

  const [openList, setOpenList] = useState(false);
  const handleChange = (e) => {
    setTempItem(e.target.value);
    if (e.target.value !== getTextByKey("select-country")) {
      autoCompleteList(e.target.value);
    }
  };

  useEffect(() => {
    setTempList(list);
  }, [openList, list]);

  const handleFocus = (e) => {
    setOpenList(true);
    setHighlightedIndex(tempList.findIndex((el) => el === tempItem));
  };

  useOnClickOutside(elementRef, () => closeList());

  const autoCompleteList = (val) => {
    setTempList(
      list.filter((item) => item.toLowerCase().startsWith(val.toLowerCase()))
    );
  };

  const closeList = () => {
    //check if item is in list
    if (list.includes(tempItem)) {
      setItem(tempItem);
    } else {
      setTempItem(item);
    }
    setTempList(list);
    setOpenList(false);
  };

  const selectItem = (item) => {
    setTempItem(item);
    setItem(item);
    setOpenList(false);
  };

  const handleBlur = () => {
    blurTimeout.current = setTimeout(() => {
      setOpenList(false);
    }, 300); // 300 ms it is enough for mouse click
  };

  const handleListMouseDown = (e) => {
    // If click on the list item — cancel closing
    if (blurTimeout.current) {
      clearTimeout(blurTimeout.current);
    }
  };

  // logic for moving through the list
  const handleKeyDown = (e) => {
    if (!openList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < tempList.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : tempList.length - 1
      );
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      selectItem(tempList[highlightedIndex]);
    }
  };

  // logic for highlighting the selected item and scrolling it into view
  useEffect(() => {
    if (
      openList &&
      highlightedIndex >= 0 &&
      itemRefs.current[highlightedIndex]
    ) {
      itemRefs.current[highlightedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex, openList]);

  return (
    <div className="select-list-element" ref={elementRef}>
      <div className="select-list-input">
        <input
          type="text"
          id={id || `input-${title}`}
          name={`input-${title}`}
          value={tempItem}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={defaultTitle}
          onKeyDown={handleKeyDown}
          aria-label={defaultTitle}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={openList}
          aria-controls={`select-listbox-${title}`}
          aria-activedescendant={
            highlightedIndex >= 0
              ? `select-option-${title}-${highlightedIndex}`
              : undefined
          }
          role="combobox"
        />
      </div>
      <div
        className={`list-container ${openList ? "open" : "close"}`}
        onMouseDown={handleListMouseDown}
        role="listbox"
      >
        {tempList.length > 0 &&
          tempList.map((item, i) => (
            <div
              key={i}
              ref={(el) => (itemRefs.current[i] = el)}
              className={`item-container ${
                i === highlightedIndex ? "active" : ""
              }`}
              onClick={() => selectItem(item)}
              id={`select-option-${title}-${i}`}
              role="option"
              aria-selected={i === highlightedIndex}
            >
              {item}
            </div>
          ))}
      </div>
    </div>
  );
};

export default SelectElement;
